import { useEffect, useState } from 'react';
import { CrossIcon, Plus } from 'assets/icons';
import Button from 'components/button';
import StyledInput from 'components/styledInput/StyledInput';
import ApiDatabase from 'server';

const MISSION_COUNT_OPTIONS = [
  { value: 'never', label: 'Aucune mission' },
  { value: 'at_least', label: 'Au moins …' },
];

const FILTER_KEYS = ['localisation', 'filterCategory', 'missionCount', 'incompleteRemuneration'];

const FILTER_LABELS = {
  localisation: 'Localisation',
  filterCategory: 'Secteur',
  missionCount: 'Nombre de missions',
  incompleteRemuneration: 'Conditions de rémunération',
};

const getDisplayValue = (key, filters, pennylaneCategoryList) => {
  switch (key) {
    case 'localisation': {
      const checked = (filters.departments || []).filter((d) => d.checked);
      if (checked.length === 0) return null;
      return checked.map((d) => d.label).join(', ');
    }
    case 'filterCategory': {
      if (!filters.filterCategory) return null;
      const cat = (pennylaneCategoryList || []).find((c) => String(c.id) === String(filters.filterCategory));
      return cat?.label ?? filters.filterCategory;
    }
    case 'missionCount': {
      if (filters.missionCountMode === 'never') return 'Aucune mission';
      if (filters.missionCountMode === 'at_least' && filters.missionCountMin) {
        return `Au moins ${filters.missionCountMin} mission(s)`;
      }
      return null;
    }
    case 'incompleteRemuneration':
      return filters.incompleteRemuneration ? 'Incomplètes' : null;
    default:
      return null;
  }
};

const clearKey = (key, filters) => {
  const next = { ...filters };
  if (key === 'missionCount') {
    next.missionCountMode = '';
    next.missionCountMin = '';
  } else if (key === 'incompleteRemuneration') {
    next.incompleteRemuneration = false;
  } else if (key === 'localisation') {
    next.departments = (filters.departments || []).map((d) => ({ ...d, checked: false }));
  } else if (key === 'filterCategory') {
    next.filterCategory = '';
  }
  return next;
};

const CompanyFilterPanel = ({
  isOpen,
  onClose,
  filters,
  setFilters,
  onApply,
  onReset,
  activeCount,
}) => {
  const [pennylaneCategoryList, setPennylaneCategoryList] = useState([]);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('xsrfToken');
    ApiDatabase.getPennyLaneCategories(
      { token },
      (data) => setPennylaneCategoryList(data?.items || []),
      (err) => { if (err.name !== 'CanceledError') console.error(err); }
    );
  }, []);

  const visibleFilterKeys = FILTER_KEYS.filter(
    (key) => key !== 'filterCategory' || pennylaneCategoryList.length > 0
  );

  if (!isOpen) return null;

  const setFilter = (patch) => setFilters((prev) => ({ ...prev, ...patch }));

  const renderBody = (key) => {
    if (key === 'localisation') {
      const list = filters.departments || [];
      const checkedLabels = list.filter((d) => d.checked).map((d) => d.label);
      return (
        <StyledInput
          type="selectMultipleCheckbox"
          placeholder="Sélectionner un département"
          list={list}
          value={
            checkedLabels.length > 0
              ? checkedLabels.join(', ')
              : 'Sélectionner un département'
          }
          onChange={(newList) => setFilter({ departments: newList })}
          isSearchable
          showChevron
          showCheck
          dropdownFullWidth
        />
      );
    }
    if (key === 'filterCategory') {
      return (
        <StyledInput
          type="select"
          value={filters.filterCategory || ''}
          list={[
            { value: '', label: 'Tous les secteurs' },
            ...pennylaneCategoryList.map((cat) => ({ value: String(cat.id), label: cat.label })),
          ]}
          onChange={(e) => {
            setFilter({ filterCategory: e.target.value });
            if (e.target.value) setExpanded(null);
          }}
        />
      );
    }
    if (key === 'missionCount') {
      return (
        <div className="flex flex-col gap-1">
          {MISSION_COUNT_OPTIONS.map((opt) => {
            const active = filters.missionCountMode === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => {
                  if (opt.value === 'never') {
                    setFilter({ missionCountMode: 'never', missionCountMin: '' });
                    setExpanded(null);
                  } else {
                    setFilter({ missionCountMode: 'at_least' });
                  }
                }}
                className={`text-left px-3 py-2 rounded-md ft-sm transition-colors ${
                  active ? 'bg-yellow-50 text-yellow-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
          {filters.missionCountMode === 'at_least' && (
            <div className="flex items-center gap-2 mt-2">
              <span className="ft-sm text-gray-600">Au moins</span>
              <input
                type="number"
                min={1}
                value={filters.missionCountMin || ''}
                onChange={(e) => setFilter({ missionCountMin: e.target.value })}
                className="w-20 h-9 px-2 ft-sm border border-gray-200 rounded-md focus:border-yellow-500 focus:outline-none"
              />
              <span className="ft-sm text-gray-600">mission(s)</span>
            </div>
          )}
        </div>
      );
    }
    if (key === 'incompleteRemuneration') {
      return (
        <div className="flex gap-2">
          {[
            { value: true, label: 'Incomplètes' },
            { value: false, label: 'Toutes' },
          ].map((opt) => {
            const active = !!filters.incompleteRemuneration === opt.value;
            return (
              <button
                key={String(opt.value)}
                onClick={() => {
                  setFilter({ incompleteRemuneration: opt.value });
                  setExpanded(null);
                }}
                className={`flex-1 py-2 px-3 rounded-md ft-sm font-medium transition-colors border ${
                  active ? 'bg-yellow-50 border-yellow-400 text-yellow-700' : 'bg-white border-gray-200 text-gray-400'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="fixed inset-0 z-40" onClick={onClose}>
      <div className="absolute inset-0 bg-gray-900 opacity-50 transition-opacity duration-300" />
      <div
        className="absolute right-0 top-0 h-full w-full max-w-[520px] bg-white shadow-xl z-50 flex flex-col animate-slideR"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between p-5 gap-5">
          <h3 className="ft-lg text-[#202124] font-semibold my-auto flex-1 min-w-0 flex items-center gap-2">
            Filtres
            {activeCount > 0 && (
              <div className="bg-yellow-100 text-yellow-800 rounded px-1 flex items-center justify-center min-w-[20px] ft-sm">
                {activeCount}
              </div>
            )}
          </h3>
          <button type="button" className="flex items-center justify-center border rounded p-1" onClick={onClose}>
            <CrossIcon wh={20} color="#111827" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="flex flex-col gap-2">
            {visibleFilterKeys.map((key, index) => {
              const isExpanded = expanded === key;
              const displayValue = getDisplayValue(key, filters, pennylaneCategoryList);
              const hasValue = displayValue !== null;
              const prevHasValue = index > 0 && getDisplayValue(visibleFilterKeys[index - 1], filters, pennylaneCategoryList) !== null;
              const isFirstInactive = index > 0 && !hasValue && prevHasValue;

              if (isExpanded) {
                return (
                  <div key={key} className="border border-yellow-400 rounded-md overflow-hidden bg-white sh-sm">
                    <div className="flex items-center justify-between px-4 py-3 bg-yellow-50">
                      <span className="ft-sm font-medium text-gray-900">{FILTER_LABELS[key]}</span>
                      <button onClick={() => setExpanded(null)} className="p-0.5 rounded hover:bg-yellow-100 transition-colors">
                        <CrossIcon wh={14} color="#374151" />
                      </button>
                    </div>
                    <div className="px-4 py-3">{renderBody(key)}</div>
                  </div>
                );
              }

              if (isFirstInactive) {
                return (
                  <div key={`divider-${key}`}>
                    <div className="my-2 border-t border-gray-200" />
                    <button
                      onClick={() => setExpanded(key)}
                      className="flex items-center justify-between w-full p-3 bg-white border border-gray-200 rounded-md hover:border-gray-300 hover:bg-gray-50 transition-all text-left"
                    >
                      <span className="ft-sm text-gray-600">{FILTER_LABELS[key]}</span>
                      <Plus wh={16} color="#9CA3AF" />
                    </button>
                  </div>
                );
              }

              if (hasValue) {
                return (
                  <div
                    key={key}
                    className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-md cursor-pointer hover:bg-yellow-100 transition-colors"
                    onClick={() => setExpanded(key)}
                  >
                    <div className="flex flex-col">
                      <span className="ft-sm font-medium text-gray-900">{FILTER_LABELS[key]}</span>
                      <span className="text-xs text-yellow-700">{displayValue}</span>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); setFilters(clearKey(key, filters)); }}
                      className="p-1 rounded hover:bg-yellow-200 transition-colors"
                    >
                      <CrossIcon wh={14} color="#374151" />
                    </button>
                  </div>
                );
              }

              return (
                <button
                  key={key}
                  onClick={() => setExpanded(key)}
                  className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-md hover:border-gray-300 hover:bg-gray-50 transition-all text-left"
                >
                  <span className="ft-sm text-gray-600">{FILTER_LABELS[key]}</span>
                  <Plus wh={16} color="#9CA3AF" />
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex-shrink-0 border-t border-gray-100 px-6 py-4 flex gap-3">
          <Button color="SECONDARY" size="SM" className="flex-1" onClick={onReset}>Réinitialiser</Button>
          <Button color="PRIMARY" size="SM" className="flex-1" onClick={onApply}>Appliquer</Button>
        </div>
      </div>
    </div>
  );
};

export default CompanyFilterPanel;
